import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { GlobalContext } from './GlobalContext';
import { useToast } from './ToastContext';
import { CloudOff, Send, Trash2, AlertTriangle } from 'lucide-react';
import { apiFetch } from './api.js';
import {
  listUnsent, removeUnsent, updateUnsent, notifyUnsentChanged, UNSENT_CHANGED,
} from './unsentPayments';

/**
 * Header tray for payments that could not be sent.
 *
 * THE ONE RULE: nothing here posts anything until a person presses Send on that
 * specific payment. Phase 2A deleted an offline queue precisely because it
 * replayed writes on its own schedule; this is the same data with the automation
 * removed. When the connection comes back the tray nudges — it does not act.
 *
 * There is deliberately no "Send all". A single button standing in for four
 * money writes is most of the way back to a machine doing it, and the realistic
 * case is one or two payments anyway.
 *
 * Hidden entirely when nothing is waiting, so it costs no header space in the
 * normal case.
 */
export default function UnsentPayments() {
  const globals = useContext(GlobalContext);
  const { getMsgBoxResult, toggleMessageBox } = globals;
  const { showToast } = useToast();
  const userId = globals.userData?.UserID;
  const socket = globals.socket;

  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const panelRef = useRef(null);

  const refresh = useCallback(() => setItems(listUnsent(userId)), [userId]);

  useEffect(() => {
    refresh();
    window.addEventListener(UNSENT_CHANGED, refresh);
    //`storage` fires in OTHER tabs, so sending from one tab clears the badge in
    //the rest instead of leaving a phantom count behind.
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(UNSENT_CHANGED, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  //Offer on reconnect, never send. A toast is the strongest thing that is still
  //honestly an offer.
  useEffect(() => {
    if (!socket) return undefined;
    const onConnect = () => {
      const waiting = listUnsent(userId);
      if (waiting.length) {
        showToast('info',
          `${waiting.length} payment${waiting.length > 1 ? 's' : ''} waiting to send — open the cloud icon in the header.`,
          6000);
      }
    };
    socket.on('connect', onConnect);
    return () => socket.off('connect', onConnect);
  }, [socket, userId, showToast]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  const money = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
  const onDay = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return String(d ?? ''); } };

  const send = async (rec, allowDuplicate = false) => {
    setSendingId(rec.id);
    try {
      //The stored body already carries the ClientRef minted when the form was
      //opened, possibly days ago. That is what makes this safe to press: if the
      //payment did reach the server before the connection died, this collapses
      //onto the same row rather than creating a second one.
      const body = allowDuplicate ? { ...rec.body, AllowDuplicate: true } : rec.body;
      const res = await apiFetch('/addcarpayment', { body });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        removeUnsent(userId, rec.id);
        notifyUnsentChanged();
        refresh();
        showToast('success', data.duplicate
          ? 'Already recorded on the server — removed from the list.'
          : 'Payment sent.');
        if (data.warning === 'overpayment') {
          toggleMessageBox({
            on: true,
            title: 'Sent — deal is now overpaid',
            message: `This deal is agreed at ${money(data.agreedPrice)} but ${money(data.totalPaid)} `
              + `has now been paid — ${money(data.excess)} more than the balance.`,
            buttons: ['OK'], clicked: '',
          });
        }
        return;
      }

      //Someone recorded a similar payment while this one sat here — which is
      //exactly the two-device story this whole feature has to survive. Ask,
      //using the same words as the live form.
      if (res.status === 409 && data.code === 'duplicate_suspected') {
        const e = data.existing || {};
        const who = e.EnteredBy ? `${e.EnteredBy} recorded` : 'Someone recorded';
        const answer = await getMsgBoxResult({
          on: true,
          title: 'Similar payment already recorded',
          message: `${who} ${money(e.Amount)} against this deal on ${onDay(e.PaymentDate)}, `
            + `while this one was waiting. Is the one you kept a separate, second payment?`,
          buttons: ['No, discard mine', 'Yes, send it'],
          clicked: '',
        });
        if (answer === 'Yes, send it') { await send(rec, true); return; }
        if (answer === 'No, discard mine') {
          removeUnsent(userId, rec.id);
          notifyUnsentChanged();
          refresh();
          showToast('success', 'Discarded — the other payment stands.');
        }
        return;
      }

      //A 4xx will never succeed on a retry. Keep the payment, but mark it so the
      //tray stops looking like it is merely waiting for a connection.
      updateUnsent(userId, rec.id, {
        state: 'failed',
        lastError: data.message || `Refused by the server (${res.status}).`,
      });
      refresh();
    } catch {
      showToast('error', 'Still no connection — the payment is safe and still waiting.');
    } finally {
      setSendingId(null);
    }
  };

  const discard = async (rec) => {
    const answer = await getMsgBoxResult({
      on: true,
      title: 'Discard this payment?',
      message: `${money(rec.label?.amount)} for deal ${rec.label?.dealAgreementNo} has never `
        + `reached the server. Discarding it here means it is gone — there is no copy anywhere else.`,
      buttons: ['Keep it', 'Discard'],
      clicked: '',
    });
    if (answer !== 'Discard') return;
    removeUnsent(userId, rec.id);
    notifyUnsentChanged();
    refresh();
  };

  if (!items.length) return null;

  const failed = items.filter((i) => i.state === 'failed').length;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 bg-white rounded-full hover:bg-gray-50 transition-all hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--theme-ring)] shadow-sm"
        aria-label={`${items.length} unsent payment${items.length > 1 ? 's' : ''}`}
        title="Payments waiting to send"
      >
        <CloudOff className="w-5 h-5 text-gray-700" />
        <span
          className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold rounded-full border-2 border-gray-100 shadow-lg"
          style={{
            background: failed ? 'var(--theme-danger)' : 'var(--theme-warning, #d97706)',
            color: 'var(--theme-danger-fg, #fff)',
          }}
        >
          {items.length}
        </span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-lg shadow-xl z-50 overflow-hidden"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
        >
          <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-border)' }}>
            <div className="font-semibold text-sm">Waiting to send</div>
            <div className="text-xs opacity-70 mt-0.5">
              Kept on this device only. Nothing is sent until you press Send.
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {items.map((rec) => (
              <div key={rec.id} className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-sm">{money(rec.label?.amount)}</span>
                  <span className="text-xs opacity-70">{onDay(rec.label?.paymentDate)}</span>
                </div>
                <div className="text-xs opacity-70 mt-0.5">
                  Deal {rec.label?.dealAgreementNo}
                  {rec.label?.sheet ? ` · ${rec.label.sheet}` : ''}
                  {` · kept ${onDay(rec.createdAt)}`}
                </div>

                {rec.state === 'failed' && (
                  <div className="flex items-start gap-1.5 mt-2 text-xs" style={{ color: 'var(--theme-danger)' }}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{rec.lastError}</span>
                  </div>
                )}

                <div className="flex gap-2 mt-2">
                  <button
                    className="btn-t flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded disabled:opacity-50"
                    disabled={sendingId === rec.id}
                    onClick={() => send(rec)}
                  >
                    <Send className="w-3.5 h-3.5" />
                    {sendingId === rec.id ? 'Sending…' : 'Send'}
                  </button>
                  <button
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded hover:bg-gray-100"
                    onClick={() => discard(rec)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Discard
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
